import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useEvents } from '../stores/events'
import { getEventStats, getTicketsForEvent, subscribeToTickets } from '@unvld/db'
import { formatEUR } from '@unvld/stripe'
import type { EventStats, Ticket } from '@unvld/db'
import { toast } from 'sonner'
import {
  ArrowLeft, Globe, Ticket as TicketIcon, Users, TrendingUp,
  Copy, ExternalLink, CheckCircle, XCircle, Clock, Pencil, Ban, Trash2,
} from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { Modal } from '@unvld/ui'

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { currentEvent, fetchEvent, publishEvent: pubEvent, cancelEvent, deleteEvent, loading } = useEvents()
  const [stats, setStats] = useState<EventStats | null>(null)
  const [tickets, setTickets] = useState<any[]>([])
  const [publishing, setPublishing] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (id) {
      fetchEvent(id)
      getEventStats(id).then(setStats).catch(console.error)
      getTicketsForEvent(id).then(setTickets).catch(console.error)

      // Realtime updates
      const channel = subscribeToTickets(id, (newTicket) => {
        setTickets((prev) => {
          const idx = prev.findIndex((t) => t.id === newTicket.id)
          if (idx >= 0) {
            const updated = [...prev]
            updated[idx] = { ...updated[idx], ...newTicket }
            return updated
          }
          return [newTicket, ...prev]
        })
        // Refresh stats
        getEventStats(id).then(setStats)
      })

      return () => { channel.unsubscribe() }
    }
  }, [id])

  if (loading || !currentEvent) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  const event = currentEvent

  const handlePublish = async () => {
    setPublishing(true)
    try {
      await pubEvent(event.id)
      toast.success('Event ist jetzt live!')
      fetchEvent(event.id)
    } catch {
      toast.error('Fehler beim Veröffentlichen')
    }
    setPublishing(false)
  }

  const handleCancel = async () => {
    setCancelling(true)
    try {
      await cancelEvent(event.id)
      toast.success('Event wurde abgesagt')
      setShowCancelModal(false)
      fetchEvent(event.id)
    } catch {
      toast.error('Fehler beim Absagen')
    }
    setCancelling(false)
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await deleteEvent(event.id)
      toast.success('Event wurde gelöscht')
      navigate('/events')
    } catch {
      toast.error('Fehler beim Löschen')
    }
    setDeleting(false)
  }

  const copyBookingLink = () => {
    navigator.clipboard.writeText(`https://app.unvld.me/events/${event.seo_slug || event.id}`)
    toast.success('Link kopiert!')
  }

  const checkedIn = stats?.total_checked_in ?? 0
  const sold = stats?.total_sold ?? 0
  const revenue = stats?.total_revenue_cents ?? 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <button onClick={() => navigate('/events')} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 mt-1">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">{event.title}</h1>
            <p className="text-slate-400 mt-1">
              {format(new Date(event.date), 'EEEE, d. MMMM yyyy', { locale: de })} · {event.doors_open} Uhr
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(event.status === 'draft' || event.status === 'published') && (
            <Link
              to={`/events/${event.id}/edit`}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm rounded-lg transition-colors"
            >
              <Pencil className="w-4 h-4" />
              Bearbeiten
            </Link>
          )}
          {event.status === 'draft' && (
            <button
              onClick={handlePublish}
              disabled={publishing}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Globe className="w-4 h-4" />
              {publishing ? 'Wird veröffentlicht...' : 'Veröffentlichen'}
            </button>
          )}
          {event.status === 'published' && (
            <>
              <button
                onClick={copyBookingLink}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm rounded-lg transition-colors"
              >
                <Copy className="w-4 h-4" />
                Buchungslink kopieren
              </button>
              <button
                onClick={() => setShowCancelModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 text-sm rounded-lg transition-colors border border-amber-600/30"
              >
                <Ban className="w-4 h-4" />
                Event absagen
              </button>
            </>
          )}
          {event.status === 'draft' && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-sm rounded-lg transition-colors border border-red-600/30"
            >
              <Trash2 className="w-4 h-4" />
              Löschen
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-slate-400 mb-1">
            <TicketIcon className="w-4 h-4" />
            <span className="text-xs">Verkauft</span>
          </div>
          <p className="text-2xl font-bold text-white">{sold} <span className="text-sm text-slate-500">/ {event.capacity}</span></p>
          <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(100, (sold / event.capacity) * 100)}%` }} />
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-slate-400 mb-1">
            <Users className="w-4 h-4" />
            <span className="text-xs">Eingecheckt</span>
          </div>
          <p className="text-2xl font-bold text-white">{checkedIn} <span className="text-sm text-slate-500">/ {sold}</span></p>
          <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full" style={{ width: sold > 0 ? `${(checkedIn / sold) * 100}%` : '0%' }} />
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-slate-400 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs">Umsatz</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatEUR(revenue)}</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-slate-400 mb-1">
            <Users className="w-4 h-4" />
            <span className="text-xs">Ratio</span>
          </div>
          <div className="space-y-1 mt-1">
            <RatioBar label="Paare" pct={stats?.ratio.couples_pct} color="bg-violet-500" />
            <RatioBar label="Herren" pct={stats?.ratio.single_male_pct} color="bg-blue-500" limit={event.ratio_limits?.single_male_pct} />
            <RatioBar label="Damen" pct={stats?.ratio.single_female_pct} color="bg-pink-500" />
          </div>
        </div>
      </div>

      {/* Price Tiers */}
      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Preiskategorien</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {event.price_tiers?.map((tier) => (
            <div key={tier.id} className="bg-slate-800 rounded-lg p-4">
              <p className="text-white font-medium">{tier.name}</p>
              <p className="text-2xl font-bold text-white mt-1">{formatEUR(tier.price_cents)}</p>
              <p className="text-xs text-slate-400 capitalize mt-1">{tier.guest_type?.replace('_', ' ')}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ticket List */}
      <section className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800">
          <h2 className="text-lg font-semibold text-white">Tickets ({tickets.length})</h2>
        </div>
        {tickets.length > 0 ? (
          <div className="overflow-auto max-h-[calc(100vh-20rem)]">
          <table className="w-full">
            <thead className="sticky top-0 z-10 bg-slate-900">
              <tr className="border-b border-slate-800">
                <th scope="col" className="text-left px-4 py-2 text-xs text-slate-400">#</th>
                <th scope="col" className="text-left px-4 py-2 text-xs text-slate-400">Gast</th>
                <th scope="col" className="text-left px-4 py-2 text-xs text-slate-400">Kategorie</th>
                <th scope="col" className="text-left px-4 py-2 text-xs text-slate-400">Status</th>
                <th scope="col" className="text-left px-4 py-2 text-xs text-slate-400">Gebucht</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {tickets.map((t) => (
                <tr key={t.id} className="hover:bg-slate-800/30">
                  <td className="px-4 py-2 text-xs text-slate-500 font-mono">{t.ticket_number}</td>
                  <td className="px-4 py-2 text-sm text-white">{t.guests?.pseudonym || '—'}</td>
                  <td className="px-4 py-2 text-sm text-slate-300">{t.price_tiers?.name}</td>
                  <td className="px-4 py-2">
                    <TicketStatusBadge status={t.status} />
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{format(new Date(t.created_at), 'dd.MM. HH:mm')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">Noch keine Tickets verkauft</div>
        )}
      </section>

      {/* Cancel Event Modal */}
      <Modal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Event absagen"
        description="Diese Aktion kann nicht rückgängig gemacht werden."
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            Bist du sicher? Alle Tickets werden storniert.
          </p>
          <div className="flex items-center gap-3 justify-end">
            <button
              onClick={() => setShowCancelModal(false)}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {cancelling ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Ban className="w-4 h-4" />
              )}
              Event absagen
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Event Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Event löschen"
        description="Diese Aktion kann nicht rückgängig gemacht werden."
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            Event unwiderruflich löschen?
          </p>
          <div className="flex items-center gap-3 justify-end">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {deleting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Endgültig löschen
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function RatioBar({ label, pct, color, limit }: { label: string; pct: number | null | undefined; color: string; limit?: number | null }) {
  const value = pct ?? 0
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-400 w-12">{label}</span>
      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden relative">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${value}%` }} />
        {limit && <div className="absolute top-0 h-full w-px bg-red-500" style={{ left: `${limit}%` }} />}
      </div>
      <span className="text-xs text-slate-500 w-10 text-right">{value.toFixed(0)}%</span>
    </div>
  )
}

function TicketStatusBadge({ status }: { status: string }) {
  const map: Record<string, { icon: any; cls: string; label: string }> = {
    valid: { icon: Clock, cls: 'text-indigo-400', label: 'Gültig' },
    checked_in: { icon: CheckCircle, cls: 'text-emerald-400', label: 'Eingecheckt' },
    cancelled: { icon: XCircle, cls: 'text-red-400', label: 'Storniert' },
    refunded: { icon: XCircle, cls: 'text-amber-400', label: 'Erstattet' },
  }
  const s = map[status] || map.valid
  const Icon = s.icon
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${s.cls}`}>
      <Icon className="w-3 h-3" />
      {s.label}
    </span>
  )
}
